import { useEffect, useRef, useState } from 'react';

/**
 * Returns `value` once it has stopped changing for `delayMs` (#325). The timer lives in an effect,
 * so every change clears the previous one and unmount clears the last. `onSettle` runs each time a
 * new value comes through (never for the first value, so a restored page or search stays).
 */
export function useDebouncedValue<T>(value: T, delayMs = 300, onSettle?: (value: T) => void): T {
  const [debounced, setDebounced] = useState(value);
  const onSettleRef = useRef(onSettle);
  useEffect(() => {
    onSettleRef.current = onSettle;
  });
  useEffect(() => {
    if (Object.is(value, debounced)) return;
    const timer = setTimeout(() => {
      setDebounced(value);
      onSettleRef.current?.(value);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs, debounced]);
  return debounced;
}
